import { useEffect, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  InfoCircledIcon,
  ReloadIcon,
  StarIcon,
} from '@radix-ui/react-icons';
import {
  userErrorMessage,
  type EbookRankingOptions,
  type EbookRankingPage,
  type EbookRankingQuery,
  type EbookRankingRow,
} from '@wx-weiqi/contracts';
import type { EbookClient } from '../services/ebooks';
import {
  beijingDay,
  rankingCalendar,
  rankingPeriodLabel,
  rankingVisibleRows,
  shiftRankingPeriod,
} from '../domain/ebook-ranking-view';
import './ebook-rankings.css';

const percent = (n: number | null) => (n === null ? '—' : `${n}%`);
export function EbookRankingResults({
  data,
  onPage,
  onPractice,
}: {
  data: EbookRankingPage;
  onPage?: (page: number) => void;
  onPractice?: () => void;
}) {
  const s = data.mine;
  const count = data.book?.questionCount;
  const pages = Math.max(1, Math.ceil(data.participants / data.pageSize));
  const first = data.items.length ? (data.page - 1) * data.pageSize + 1 : 0;
  return (
    <>
      <section className="ebook-ranking-mine" aria-label="我的表现">
        <div className="ebook-ranking-mine-heading">
          <span className="ebook-ranking-eyebrow">我的表现</span>
          <h2>{s.name}</h2>
        </div>
        <div className="ebook-ranking-position">
          {s.rank === null ? (
            <strong>等待上榜</strong>
          ) : (
            <strong>
              第 <b>{s.rank}</b> 名
            </strong>
          )}
          <span>{data.participants} 人参与</span>
        </div>
        <dl className="ebook-ranking-metrics">
          <div>
            <dt>独立首答正确</dt>
            <dd>
              {s.firstCorrect}
              <small> 题</small>
            </dd>
          </div>
          <div>
            <dt>{data.book ? '累计完成' : '新完成'}</dt>
            <dd>
              {s.completed}
              <small> 题</small>
            </dd>
          </div>
          <div>
            <dt>首答正确率</dt>
            <dd>{percent(s.accuracy)}</dd>
          </div>
          <div>
            <dt>练习天数</dt>
            <dd>
              {s.practiceDays}
              <small> 天</small>
            </dd>
          </div>
          <div>
            <dt>复习题数</dt>
            <dd>
              {s.reviewed}
              <small> 题</small>
            </dd>
          </div>
          <div>
            <dt>待批改</dt>
            <dd>
              {s.pending}
              <small> 题</small>
            </dd>
          </div>
        </dl>
        {count !== undefined && (
          <div className="ebook-ranking-progress">
            <span>
              本书进度{' '}
              <strong>
                {s.completed} / {count} 题
              </strong>
            </span>
            <progress max={Math.max(1, count)} value={s.completed} aria-label="本书完成进度" />
          </div>
        )}
        <p className="ebook-ranking-pending">待批改不按错题计算。</p>
        <button type="button" className="ebook-ranking-primary" onClick={onPractice}>
          继续练习 <ArrowRightIcon aria-hidden="true" />
        </button>
      </section>
      <section className="ebook-ranking-list" aria-label="排名列表">
        <div className="ebook-ranking-list-heading">
          <div>
            <h2>
              {data.book ? '本书学习榜' : data.period === 'WEEK' ? '每周学习榜' : '每月学习榜'}
            </h2>
            <p>按独立首答正确题数排名 · 同分并列</p>
          </div>
          <span>
            {data.participants} 人参与 / {data.eligibleStudents} 人可参与
          </span>
        </div>
        <div className="ebook-ranking-table-area">
          {!data.classId ? (
            <div className="ebook-ranking-empty">
              <h3>加入班级，和伙伴一起进步</h3>
              <p>老师将你加入班级后，这里会展示班级排名。</p>
            </div>
          ) : !data.items.length ? (
            <div className="ebook-ranking-empty">
              <h3>从一道题开始，积累今天的进步</h3>
              <p>这个范围还没有有效练习记录，完成练习后即可参与。</p>
            </div>
          ) : (
            <table>
              <caption className="ebook-ranking-sr-only">
                班级学习排名，同分并列，不以作答速度排名
              </caption>
              <thead>
                <tr>
                  <th scope="col">排名</th>
                  <th scope="col">小棋手</th>
                  <th scope="col">独立首答正确</th>
                  <th scope="col">{data.book ? '累计完成' : '新完成'}</th>
                  <th scope="col">首答正确率</th>
                  <th scope="col">{data.book ? '完成率' : '练习天数'}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((row, index) => (
                  <RankingRow key={`${data.page}-${index}`} row={row} total={count} />
                ))}
              </tbody>
            </table>
          )}
        </div>
        <footer className="ebook-ranking-list-footer">
          <div className="ebook-ranking-pagination">
            <span className="ebook-ranking-range">
              显示 {first} — {first ? first + data.items.length - 1 : 0} / {data.participants} 人
            </span>
            <nav aria-label="排名分页" tabIndex={-1}>
              <button
                type="button"
                data-direction="previous"
                disabled={data.page <= 1}
                onClick={() => onPage?.(data.page - 1)}
              >
                <ArrowLeftIcon aria-hidden="true" />
                <span>上一页</span>
              </button>
              <span>
                第 {data.page}/{pages} 页
              </span>
              <button
                type="button"
                data-direction="next"
                disabled={data.page >= pages}
                onClick={() => onPage?.(data.page + 1)}
              >
                <span>下一页</span>
                <ArrowRightIcon aria-hidden="true" />
              </button>
            </nav>
          </div>
          <div className="ebook-ranking-updated">
            {data.legacyTimingExcluded && <span>部分旧记录仅计入单书累计。</span>}
            <span>
              更新于{' '}
              {new Intl.DateTimeFormat('zh-CN', {
                timeZone: 'Asia/Shanghai',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(data.asOf))}{' '}
              · 北京时间
            </span>
          </div>
        </footer>
      </section>
    </>
  );
}
function RankingRow({ row, total }: { row: EbookRankingRow; total: number | undefined }) {
  return (
    <tr className={row.isMe ? 'is-me' : undefined}>
      <td data-label="排名">
        <span className={`ebook-ranking-number${row.rank && row.rank <= 3 ? ' is-leading' : ''}`}>
          {row.rank}
        </span>
      </td>
      <th scope="row">
        {row.name}
        {row.isMe && <span className="ebook-ranking-me-badge">[我]</span>}
      </th>
      <td data-label="独立首答正确">
        <strong>{row.firstCorrect}</strong> 题
      </td>
      <td data-label={total === undefined ? '新完成' : '累计完成'}>{row.completed} 题</td>
      <td data-label="首答正确率">{percent(row.accuracy)}</td>
      <td data-label={total === undefined ? '练习天数' : '完成率'}>
        {total === undefined
          ? `${row.practiceDays} 天`
          : percent(total ? Math.round((row.completed / total) * 100) : 0)}
      </td>
    </tr>
  );
}
export function EbookRankings({
  client,
  onBack,
  onOpenBook,
}: {
  client: Pick<EbookClient, 'rankingOptions' | 'rankings'>;
  onBack: () => void;
  onOpenBook: (id: string) => void;
}) {
  const [options, setOptions] = useState<EbookRankingOptions | null>(null);
  const [data, setData] = useState<EbookRankingPage | null>(null);
  const [period, setPeriod] = useState<EbookRankingQuery['period']>('WEEK');
  const [classId, setClassId] = useState('');
  const [releaseId, setReleaseId] = useState('');
  const [date, setDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [optionsError, setOptionsError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<'rules' | 'date' | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const paginationFocus = useRef<'previous' | 'next' | null>(null);
  useEffect(() => {
    if (!data || !paginationFocus.current) return;
    const target = content.current?.querySelector<HTMLButtonElement>(
      `button[data-direction="${paginationFocus.current}"]`,
    );
    if (target && !target.disabled) target.focus();
    else content.current?.querySelector<HTMLElement>('[aria-label="排名分页"]')?.focus();
    paginationFocus.current = null;
  }, [data]);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  useEffect(() => {
    const element = content.current;
    if (!element) return;
    let timer: ReturnType<typeof setTimeout>;
    let lastSize: number | null = null;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = rankingVisibleRows(window.innerWidth, element.clientHeight);
        if (next !== lastSize) {
          lastSize = next;
          paginationFocus.current = null;
          setPage(1);
          setPageSize(next);
        }
      }, 120);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    let alive = true;
    setOptions(null);
    setOptionsError('');
    setData(null);
    void client
      .rankingOptions()
      .then((value) => {
        if (!alive) return;
        setOptions(value);
        setClassId((id) =>
          value.classes.some((c) => c.id === id) ? id : (value.classes[0]?.id ?? ''),
        );
        setReleaseId((id) =>
          value.books.some((b) => b.releaseId === id) ? id : (value.books[0]?.releaseId ?? ''),
        );
      })
      .catch((e: unknown) => {
        if (alive) setOptionsError(userErrorMessage(e, '排名信息暂未加载，请重试。'));
      });
    return () => {
      alive = false;
    };
  }, [client, refresh]);
  useEffect(() => {
    if (!options || pageSize === null || (period === 'BOOK' && !releaseId)) return;
    let alive = true;
    setLoading(true);
    setError('');
    setData(null);
    void client
      .rankings({
        period,
        page,
        pageSize,
        ...(classId ? { classId } : {}),
        ...(period === 'BOOK' ? { releaseId } : date ? { date } : {}),
      })
      .then((value) => {
        if (alive) {
          setData(value);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (alive) {
          setError(userErrorMessage(e, '排名暂未加载，请重试。'));
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [client, options, period, classId, releaseId, date, page, pageSize]);
  const change = (fn: () => void) => {
    paginationFocus.current = null;
    setPage(1);
    fn();
  };
  const retry = () => setRefresh((n) => n + 1);
  const today = beijingDay(data ? new Date(data.asOf) : undefined);
  const selectedDay = date || today;
  const calendarPeriod = period === 'MONTH' ? 'MONTH' : 'WEEK';
  const atCurrent =
    rankingCalendar(calendarPeriod, selectedDay).start >=
    rankingCalendar(calendarPeriod, today).start;
  const changePeriod = (direction: -1 | 1) =>
    change(() => setDate(shiftRankingPeriod(calendarPeriod, selectedDay, direction)));
  return (
    <main className="ebook-rankings">
      <header className="ebook-ranking-header">
        <div>
          <span className="ebook-ranking-eyebrow">
            <StarIcon aria-hidden="true" /> 一起思考，一起成长
          </span>
          <h1 ref={heading} tabIndex={-1}>
            学习排名
          </h1>
          <p>认真思考，每天进步一点点。</p>
        </div>
        <button type="button" onClick={onBack}>
          <ArrowLeftIcon aria-hidden="true" /> 返回书架
        </button>
      </header>
      <div className="ebook-ranking-toolbar">
        <div className="ebook-ranking-tabs" role="group" aria-label="排名类型">
          {(
            [
              ['WEEK', '周排名'],
              ['MONTH', '月排名'],
              ['BOOK', '单书排名'],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={period === value}
              onClick={() => change(() => setPeriod(value))}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ebook-ranking-filters">
          <label className="ebook-ranking-class">
            <span>我的班级</span>
            <select
              aria-label="我的班级"
              value={classId}
              disabled={!options?.classes.length}
              onChange={(e) => change(() => setClassId(e.target.value))}
            >
              {!options?.classes.length && (
                <option value="">{options ? '暂未加入班级' : '加载中…'}</option>
              )}
              {options?.classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          {period === 'BOOK' ? (
            <label className="ebook-ranking-book">
              <span>选择电子书</span>
              <select
                aria-label="选择电子书"
                value={releaseId}
                disabled={!options?.books.length}
                onChange={(e) => change(() => setReleaseId(e.target.value))}
              >
                {!options?.books.length && <option value="">暂无授权电子书</option>}
                {options?.books.map((b) => (
                  <option value={b.releaseId} key={b.releaseId}>
                    {b.title}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div className="ebook-ranking-calendar" role="group" aria-label="排名周期">
              <span className="ebook-ranking-calendar-label">
                {period === 'WEEK' ? '选择周次' : '选择月份'}
              </span>
              <div className="ebook-ranking-calendar-stepper">
                <button
                  type="button"
                  aria-label={period === 'WEEK' ? '上一周' : '上个月'}
                  onClick={() => changePeriod(-1)}
                >
                  <ChevronLeftIcon aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="ebook-ranking-date"
                  aria-label="选择日期"
                  onClick={() => setDialog('date')}
                >
                  {rankingPeriodLabel(calendarPeriod, selectedDay)}
                </button>
                <button
                  type="button"
                  aria-label={period === 'WEEK' ? '下一周' : '下个月'}
                  disabled={atCurrent}
                  onClick={() => changePeriod(1)}
                >
                  <ChevronRightIcon aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                className="ebook-ranking-current"
                onClick={() => change(() => setDate(''))}
              >
                {period === 'WEEK' ? '本周' : '本月'}
              </button>
            </div>
          )}
          <div className="ebook-ranking-tools">
            <button type="button" aria-label="排名规则" onClick={() => setDialog('rules')}>
              <InfoCircledIcon aria-hidden="true" />
              <span>排名规则</span>
            </button>
            <button type="button" aria-label="刷新排名" onClick={retry}>
              <ReloadIcon aria-hidden="true" />
              <span>刷新</span>
            </button>
          </div>
        </div>
      </div>
      <div
        ref={content}
        className="ebook-ranking-content"
        aria-busy={!optionsError && (!options || (loading && !(period === 'BOOK' && !releaseId)))}
      >
        {optionsError || error ? (
          <div className="ebook-ranking-error" role="alert">
            {optionsError || error}
            <button type="button" onClick={retry}>
              重新加载
            </button>
          </div>
        ) : options && period === 'BOOK' && !releaseId ? (
          <div className="ebook-ranking-empty ebook-ranking-state">
            <h2>先打开一本书，开启学习旅程</h2>
            <p>老师发布并授权电子书后，即可查看单书排名。</p>
          </div>
        ) : !options || loading || !data ? (
          <div className="ebook-ranking-state" role="status">
            正在统计学习表现…
          </div>
        ) : (
          <EbookRankingResults
            data={data}
            onPage={(next) => {
              paginationFocus.current = next > data.page ? 'next' : 'previous';
              setPage(next);
            }}
            onPractice={() => (period === 'BOOK' && releaseId ? onOpenBook(releaseId) : onBack())}
          />
        )}
      </div>
      {dialog && (
        <RankingDialog
          kind={dialog}
          day={selectedDay}
          today={today}
          onClose={() => setDialog(null)}
          onDate={(value) => {
            change(() => setDate(value));
            setDialog(null);
          }}
        />
      )}
    </main>
  );
}
function RankingDialog({
  kind,
  day,
  today,
  onClose,
  onDate,
}: {
  kind: 'rules' | 'date';
  day: string;
  today: string;
  onClose: () => void;
  onDate: (value: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const element = ref.current;
    const previous = document.activeElement;
    element?.showModal();
    return () => {
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="ebook-ranking-dialog"
      aria-labelledby="ranking-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header>
        <h2 id="ranking-dialog-title">{kind === 'rules' ? '排名规则' : '选择日期'}</h2>
        <button type="button" aria-label="关闭" onClick={onClose}>
          <Cross2Icon aria-hidden="true" />
        </button>
      </header>
      {kind === 'rules' ? (
        <ul>
          <li>周榜从周一零点开始，月榜从每月 1 日零点开始，均按北京时间和实际完成时间统计。</li>
          <li>
            周榜、月榜统计本班学生各自获授权的电子书练习。相同题目跨出版版本只算一次首答；重复作答计入复习，不重复加分。
          </li>
          <li>独立首答正确题数越多，排名越靠前。同分并列，不比较作答速度。</li>
          <li>首答用过提示或看过答案，不计独立正确。正确率只计算独立首答中已判定的正确和错误。</li>
          <li>待批改和未知结果不按错题计算；老师批改后回填原周期。打开题目但未落子提交不计入。</li>
          <li>单书榜为累计排名，只比较同班、当前获授权相同题目集合的同学。复习按不同题目计数。</li>
          <li>没有完成时间的旧记录仅参与单书累计，不计周/月榜和练习天数。</li>
        </ul>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const value = new FormData(event.currentTarget).get('rankingDate');
            if (typeof value === 'string' && value && value <= today) onDate(value);
          }}
        >
          <label>
            查看这一天所在的周或月
            <input type="date" name="rankingDate" defaultValue={day} max={today} required />
          </label>
          <button className="ebook-ranking-primary" type="submit">
            查看排名
          </button>
        </form>
      )}
    </dialog>
  );
}
